import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import { useContext } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContextOperation } from "../../context/OperationContext";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import useSnackbar from "../../hooks/useSnackbar";
import { IVehicleExitState } from "../../page/VehicleExitPage";
import VehicleExitService from "./service/VehicleExitService";
import useTenant from "../../hooks/useTenant";

interface FormInputs {
  plate: string;
}

const VehicleExitSearch = () => {
  const navigate = useNavigate();
  const { tenant, setLoading } = useContext(ContextOperation);
  const snackbar = useSnackbar();
  const t = useOperationTranslation();

  const { TenantAutocomplete } = useTenant();

  const form = useForm<FormInputs>();

  const onSubmit = (data: FormInputs) => {
    setLoading(true);
    VehicleExitService.get.vehicleByPlate(tenant, data.plate).then(
      (response) => {
        setLoading(false);
        if (response.physicalLocationId) {
          navigate(`/operation/transfer/vehiclesExit/status`, {
            state: {
              ...response,
              tenant: tenant,
              id: response?.id,
              plate: response?.plate,
              modelType: `${response?.model.brand.descr} ${response?.model.descr}`,
              from: response?.checkinLastLocationId,
              fromType: response?.checkinLastLocationType,
              referenceLocation: `${
                response?.referenceLocationType === "LMS"
                  ? "DMS"
                  : response.referenceLocationType
              } - ${response?.referenceLocationDescription}`,
            } as IVehicleExitState,
          });
        } else {
          snackbar.show("warning", t("vehicleExit.search.searchError"));
        }
      },
      (reject) => {
        setLoading(false);
        snackbar.show(
          "warning",
          t("vehicleExit.search.notFound").replace("#plateNotFound", data.plate)
        );
      }
    );
  };

  return (
    <Mui.Box>
      <form id="vehicle-exit-search" onSubmit={form.handleSubmit(onSubmit)}>
        <Mui.Box
          height="calc(100vh - 48px - 88px - 32px)"
          padding={{ md: 2, xs: 0 }}
        >
          <Mui.Card style={{ height: "100%" }}>
            <Mui.CardContent style={{ height: "100%" }}>
              <Mui.Stack height="calc(100% - 32px)" overflow="auto">
                <Mui.Typography variant="h4" textAlign="center" mb={2}>
                  {t("vehicleExit.search.content.title")}
                </Mui.Typography>

                <Mui.Typography
                  variant="body"
                  whiteSpace="pre-line"
                  textAlign="center"
                  mb="80px"
                >
                  {t("vehicleExit.search.content.description")}
                </Mui.Typography>

                <Mui.Grid container  columns={12} p="0px 16px" mb="25px" spacing={2}>
                  {TenantAutocomplete}

                  <Mui.Grid 
                    item
                    xl={TenantAutocomplete ? 9 : 12} 
                    lg={TenantAutocomplete ? 9 : 12} 
                    md={TenantAutocomplete ? 6 : 12} 
                    xs={12}
                  >
                    <Mui.TextField
                      label={t("vehicleExit.search.content.inputs.plate.label")}
                      variant="standard"
                      placeholder={t(
                        "vehicleExit.search.content.inputs.plate.placeholder"
                      )}
                      {...form.register("plate")}
                      value={form.watch("plate") || ""}
                      fullWidth
                    />
                  </Mui.Grid>
                </Mui.Grid>

                <Mui.Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="center"
                  mb="25px"
                >
                  <Mui.Typography variant="body">
                    {t("vehicleExit.search.content.scanText")}
                  </Mui.Typography>
                  <MuiIcons.InfoOutlined style={{ marginLeft: "5px" }} />
                </Mui.Stack>

                <Mui.Button
                  variant="contained"
                  color="accent"
                  disabled={true}
                  style={{ margin: "0px 16px" }}
                >
                  {t("vehicleExit.search.content.buttonScan")}{" "}
                  <MuiIcons.QrCode />
                </Mui.Button>

                <Mui.Button
                  variant="contained"
                  color="accent"
                  type="submit"
                  style={{ margin: "auto 16px 0px 16px" }}
                >
                  {t("vehicleExit.search.content.buttonSearch")}
                </Mui.Button>
              </Mui.Stack>
            </Mui.CardContent>
          </Mui.Card>
        </Mui.Box>
      </form>
    </Mui.Box>
  );
};

export default VehicleExitSearch;
